import React, { useState, useEffect } from 'react';
import {
  fetchCategories,
  fetchWordsByCategory,
  fetchWordsAnyCategory,
  parseManualWords,
  checkBackendAvailability
} from '../services/WordService';
import { generatePDF, generateAnswerKeyPDF } from './PDFExporter';
import { generateGrid } from './GridGenerator';
import './Generator.css';

function Generator() {
  const [categories, setCategories] = useState([]);
  const [words, setWords] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [limit, setLimit] = useState('');
  const [title, setTitle] = useState('Word Search Puzzle');
  const [tab, setTab] = useState('custom'); // Default to custom words
  const [error, setError] = useState(null);
  const [batchEnabled, setBatchEnabled] = useState(false);
  const [batchCount, setBatchCount] = useState(1);
  const [generateAnswerKey, setGenerateAnswerKey] = useState(false);
  const [customWords, setCustomWords] = useState('');
  const [backendAvailable, setBackendAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initialize = async () => {
      setIsLoading(true);
      const available = await checkBackendAvailability();
      setBackendAvailable(available);

      if (available) {
        try {
          const data = await fetchCategories();
          setCategories(data);
        } catch (err) {
          // Silently handle - categories just won't be available
        }
      }
      setIsLoading(false);
    };

    initialize();
  }, []);

  const handleTabChange = (selectedTab) => {
    setTab(selectedTab);
    setWords([]);
    setError(null);
  };

  const loadWordsFromBackend = async () => {
    try {
      const parsedLimit = limit ? parseInt(limit, 10) : 10;
      const data =
        tab === 'category'
          ? await fetchWordsByCategory(selectedCategory, parsedLimit)
          : await fetchWordsAnyCategory(parsedLimit);
      setWords(data);
      return data;
    } catch (err) {
      setError('Failed to fetch words');
      return [];
    }
  };

  const handleGenerate = async () => {
    let fetchedWords;

    if (tab === 'custom') {
      fetchedWords = parseManualWords(customWords);
      if (fetchedWords.length < 3) {
        setError('Please enter at least 3 words to generate a puzzle');
        return;
      }
    }

    setError(null);

    if (batchEnabled) {
      for (let i = 1; i <= batchCount; i++) {
        if (tab !== 'custom') {
          fetchedWords = await loadWordsFromBackend();
        }
        if (fetchedWords.length === 0) {
          setError('No words available to generate puzzle');
          return;
        }
        const { grid, placedWords } = generateGrid(fetchedWords, 15);
        const batchTitle = `${title} #${i}`;
        generatePDF(grid, placedWords, batchTitle);
        if (generateAnswerKey) {
          generateAnswerKeyPDF(grid, placedWords, batchTitle);
        }
      }
    } else {
      if (tab !== 'custom') {
        fetchedWords = await loadWordsFromBackend();
      }
      if (fetchedWords.length === 0) {
        setError('No words available to generate puzzle');
        return;
      }
      const { grid, placedWords } = generateGrid(fetchedWords, 15);
      generatePDF(grid, placedWords, title);
      if (generateAnswerKey) {
        generateAnswerKeyPDF(grid, placedWords, title);
      }
    }

    setWords(fetchedWords);
  };

  if (isLoading) {
    return (
      <div className="generator">
        <h1 className="generator-title">Word Search Generator</h1>
        <p style={{ textAlign: 'center' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="generator">
      <h1 className="generator-title">Word Search Generator</h1>

      {error && <p className="error">{error}</p>}

      <div className="form-group">
        <label htmlFor="title">Puzzle Title:</label>
        <input
          type="text"
          id="title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="input"
          placeholder="Enter puzzle title"
        />
      </div>

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={batchEnabled}
            onChange={(e) => setBatchEnabled(e.target.checked)}
          />
          Generate Multiple Puzzles
        </label>
      </div>

      {batchEnabled && (
        <div className="form-group">
          <label htmlFor="batch-count">Number of Puzzles:</label>
          <input
            type="number"
            id="batch-count"
            value={batchCount}
            onChange={(e) => setBatchCount(e.target.value)}
            className="input"
            placeholder="Enter number of puzzles"
            min="1"
            max="50"
          />
        </div>
      )}

      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={generateAnswerKey}
            onChange={(e) => setGenerateAnswerKey(e.target.checked)}
          />
          Generate Answer Key
        </label>
      </div>

      <div className="tabs">
        <button
          className={`tab ${tab === 'custom' ? 'active' : ''}`}
          onClick={() => handleTabChange('custom')}
        >
          Custom Words
        </button>
        {backendAvailable && (
          <>
            <button
              className={`tab ${tab === 'category' ? 'active' : ''}`}
              onClick={() => handleTabChange('category')}
            >
              Category-Based
            </button>
            <button
              className={`tab ${tab === 'random' ? 'active' : ''}`}
              onClick={() => handleTabChange('random')}
            >
              Random Words
            </button>
          </>
        )}
      </div>

      {tab === 'custom' && (
        <div>
          <div className="form-group">
            <label htmlFor="custom-words">Enter Your Words:</label>
            <textarea
              id="custom-words"
              value={customWords}
              onChange={(e) => setCustomWords(e.target.value)}
              className="input textarea"
              placeholder="Enter words separated by commas, semicolons, or new lines

Example:
apple, banana, cherry
dog
elephant"
              rows={10}
            />
            <small className="help-text">
              Words can be up to 15 characters. Only letters allowed.
            </small>
          </div>
        </div>
      )}

      {tab === 'category' && backendAvailable && (
        <div>
          <div className="form-group">
            <label htmlFor="categories">Select a Category:</label>
            <select
              id="categories"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="dropdown"
            >
              <option value="">-- Select a Category --</option>
              {categories.map((category, index) => (
                <option key={index} value={category.Category}>
                  {category.Category}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="limit">Word Limit:</label>
            <input
              type="number"
              id="limit"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="input"
              placeholder="Enter limit"
            />
          </div>
          <button onClick={loadWordsFromBackend} className="button secondary">
            Load Words
          </button>
        </div>
      )}

      {tab === 'random' && backendAvailable && (
        <div>
          <div className="form-group">
            <label htmlFor="limit">Word Limit:</label>
            <input
              type="number"
              id="limit"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="input"
              placeholder="Enter limit"
            />
          </div>
          <button onClick={loadWordsFromBackend} className="button secondary">
            Load Random Words
          </button>
        </div>
      )}

      {words.length > 0 && (
        <>
          <h2 className="section-title">Words ({words.length})</h2>
          <ul className="word-list">
            {words.map((word, index) => (
              <li key={index} className="word-item">
                {word.Word}
              </li>
            ))}
          </ul>
        </>
      )}

      <button onClick={handleGenerate} className="button generate-button">
        {batchEnabled ? 'Generate Word Search Batch' : 'Generate Word Search PDF'}
      </button>
    </div>
  );
}

export default Generator;
